import React, { useState } from "react";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import useMediaQuery from "@mui/material/useMediaQuery";
import { GridHeader } from "../../shared/GridHeader";
import EAButton from "../../shared/Buttons/EAButton";
import EAIconButton from "../../shared/Buttons/EAIconButton";
import SwimmingLessonCard from "./SwimmingLessonCard";
import LoadableComponent from "../../../../features/Loading/LoadableComponent";
import { AcademyGroupingVariant, SwimmingInstructor, SwimmingLesson } from "../../../models/SwimmingScheduleData";
import "./style.scss";

interface IProps {
  columns: string[];
  lessons: SwimmingLesson[];
  visibleHours: string[];
  hasPastHours: boolean;
  onShowPastHours: () => void;
  onLessonClick: (lesson: SwimmingLesson) => void;
  loading?: boolean;
  groupingVariant?: AcademyGroupingVariant;
  instructors?: SwimmingInstructor[];
  firstAvailableDate?: Date | null;
  firstTimeLoading?: boolean;
  firstTimeChecked?: boolean;
  onGoToNearestDate?: () => void;
}

const TIME_COLUMN_WIDTH = "72px";

const SwimmingGrid: React.FC<IProps> = ({
  columns,
  lessons,
  visibleHours,
  hasPastHours,
  onShowPastHours,
  onLessonClick,
  loading = false,
  groupingVariant = AcademyGroupingVariant.ByLanes,
  instructors = [],
  firstAvailableDate = null,
  firstTimeLoading = false,
  firstTimeChecked = false,
  onGoToNearestDate,
}) => {
  const [currentColumnIndex, setCurrentColumnIndex] = useState(0);
  const isMobile = useMediaQuery("(max-width: 980px)");

  const isSingleColumn = columns.length === 1;
  const displayedColumns =
    isMobile && columns.length > 1 ? [columns[Math.min(currentColumnIndex, columns.length - 1)]] : columns;

  const safeIndex = Math.min(currentColumnIndex, columns.length - 1);
  const canGoPrevious = safeIndex > 0;
  const canGoNext = safeIndex < columns.length - 1;

  const gridTemplateColumns = `${TIME_COLUMN_WIDTH} repeat(${displayedColumns.length}, minmax(160px, 1fr))`;

  const findLessonsForSlot = (column: string, hour: string): SwimmingLesson[] =>
    lessons.filter((lesson) => {
      if (!lesson.startTime_str.startsWith(hour.slice(0, 2))) return false;
      if (groupingVariant === AcademyGroupingVariant.ByAssortmentGroups) return lesson.exerciseGroupName === column;
      if (groupingVariant === AcademyGroupingVariant.ByInstructors)
        return `${lesson.humanName ?? ""} ${lesson.humanLastName ?? ""}`.trim() === column;
      return lesson.location === column;
    });

  const renderColumnHeader = (column: string) => {
    if (groupingVariant === AcademyGroupingVariant.ByInstructors) {
      const instr = instructors.find((i) => `${i.name} ${i.lastName}`.trim() === column);
      return (
        <div className="swimming-grid__instructor-header">
          {instr?.picture ? (
            <img src={instr.picture} className="swimming-grid__instructor-avatar" alt={column} />
          ) : (
            <div className="swimming-grid__instructor-avatar swimming-grid__instructor-avatar--placeholder" />
          )}
          <span>{column}</span>
        </div>
      );
    }
    return <GridHeader title={column} />;
  };

  const renderHeader = () => {
    if (isMobile && columns.length > 1) {
      return (
        <div className="swimming-grid__header--mobile-nav">
          <div className="swimming-grid__mobile-nav">
            <EAIconButton
              title="Poprzedni"
              icon={<MdChevronLeft />}
              variant="icon"
              onClick={() => setCurrentColumnIndex((i) => Math.max(0, i - 1))}
              disabled={!canGoPrevious}
            />
            <div className="swimming-grid__mobile-nav-content">
              <span className="swimming-grid__mobile-nav-name">{columns[safeIndex]}</span>
              <span className="swimming-grid__mobile-nav-counter">
                {safeIndex + 1}/{columns.length}
              </span>
            </div>
            <EAIconButton
              title="Następny"
              icon={<MdChevronRight />}
              variant="icon"
              onClick={() => setCurrentColumnIndex((i) => Math.min(columns.length - 1, i + 1))}
              disabled={!canGoNext}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="swimming-grid__header" style={{ gridTemplateColumns }}>
        <div className="swimming-grid__time-header">Godzina</div>
        {displayedColumns.map((column) => (
          <div key={column} className="swimming-grid__location-header">
            {renderColumnHeader(column)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <LoadableComponent loading={loading}>
      {columns.length === 0 && !loading ? (
        <div className="swimming-grid__empty-state">
          <p className="swimming-grid__empty-state-text">Brak zajęć dla wybranych filtrów.</p>
          {(firstTimeLoading || firstTimeChecked) && (
            <div className="swimming-grid__empty-state-actions">
              {firstAvailableDate && firstTimeChecked && (
                <p className="swimming-grid__empty-state-hint">
                  Najbliższy dostępny termin: {format(firstAvailableDate, "d MMMM yyyy", { locale: pl })}
                </p>
              )}
              <EAButton
                title={
                  firstTimeLoading
                    ? "Szukam terminu..."
                    : firstAvailableDate
                      ? "Wyszukaj najbliższą datę"
                      : "Nie znaleziono terminów w ciągu najbliższego roku"
                }
                onClick={onGoToNearestDate ?? (() => {})}
                disabled={!firstAvailableDate || firstTimeLoading}
                loading={firstTimeLoading}
                loadingText="Szukam terminu..."
                variant="outlined"
              />
            </div>
          )}
        </div>
      ) : (
        <div className="swimming-grid">
          {renderHeader()}

          <div className="swimming-grid__separator" />

          {hasPastHours && (
            <div className="swimming-grid__past-hours-row">
              <EAButton
                title="Pokaż wcześniejsze godziny"
                onClick={onShowPastHours}
                variant="caption"
                color="dark"
                compact
              />
            </div>
          )}

          <div className="swimming-grid__body">
            {visibleHours.map((hour) => (
              <div key={hour} className="swimming-grid__row" style={{ gridTemplateColumns }}>
                <div className="swimming-grid__time-cell">
                  <p className="swimming-grid__time-text">{hour}</p>
                </div>
                {displayedColumns.map((column) => {
                  const slotLessons = findLessonsForSlot(column, hour);
                  return (
                    <div
                      key={`${column}-${hour}`}
                      className={`swimming-grid__slot-cell${isSingleColumn ? " swimming-grid__slot-cell--single-column" : ""}`}
                    >
                      {slotLessons.map((lesson) => (
                        <SwimmingLessonCard
                          key={`${lesson.objectDb}-${lesson.objectId}`}
                          lesson={lesson}
                          onClick={onLessonClick}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </LoadableComponent>
  );
};

export default SwimmingGrid;
